import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "../components/Layout";
import { api, formatINR, API, TOKEN_KEY } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Download, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import ReconciliationPanel from "../components/ReconciliationPanel";

export default function Reports() {
  const [tab, setTab] = useState("eod");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [exp, setExp] = useState({ from: "", to: "", site: "", category: "" });
  const [exporting, setExporting] = useState(false);
  const [drilldown, setDrilldown] = useState(null); // { site, data }
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const openDrilldown = useCallback(async (site) => {
    setDrilldown({ site, data: null });
    setDrilldownLoading(true);
    try {
      const r = await api.get("/reports/site-drilldown", { params: { site, period: tab } });
      setDrilldown({ site, data: r.data });
    } catch (e) {
      toast.error("Could not load site detail");
      setDrilldown(null);
    } finally { setDrilldownLoading(false); }
  }, [tab]);

  const load = async (t) => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/${t}`);
      setData((d) => ({ ...d, [t]: r.data }));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // EOD/EOW/EOM tabs share the load(); the recon tab self-fetches.
    if (tab === "recon") return;
    if (!data[tab]) load(tab);
    /* eslint-disable-next-line */
  }, [tab]);

  useEffect(() => {
    api.get("/sites").then((r) => {
      setSites(r.data.sites || []);
      setCategories(r.data.categories || []);
    }).catch(() => {});
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exp.from) params.set("from", exp.from);
      if (exp.to) params.set("to", exp.to);
      if (exp.site) params.set("site", exp.site);
      if (exp.category) params.set("category", exp.category);
      // Use fetch directly to handle blob with Bearer auth
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/reports/export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.href = url;
      a.download = m ? m[1] : "vow_requests.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally { setExporting(false); }
  };

  const d = data[tab];

  return (
    <AppShell back="/dashboard" title="REPORTS">
      <div className="px-5 py-5 lg:px-10 lg:py-8 space-y-6">
        <h2 className="font-semibold text-2xl lg:text-3xl tracking-tight uppercase">Reports</h2>

        {/* Export panel */}
        <div data-testid="export-panel" className="bg-white border border-slate-200 p-4 lg:p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-micro">EXPORT CSV</div>
              <div className="font-mono text-[10px] text-slate-500 mt-0.5">Filter by date range, site, and category. Opens in Excel.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <div className="label-micro mb-1">FROM</div>
              <input
                data-testid="exp-from"
                type="date"
                value={exp.from}
                onChange={(e) => setExp((s) => ({ ...s, from: e.target.value }))}
                className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none"
              />
            </div>
            <div>
              <div className="label-micro mb-1">TO</div>
              <input
                data-testid="exp-to"
                type="date"
                value={exp.to}
                onChange={(e) => setExp((s) => ({ ...s, to: e.target.value }))}
                className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none"
              />
            </div>
            <div>
              <div className="label-micro mb-1">SITE</div>
              <select
                data-testid="exp-site"
                value={exp.site}
                onChange={(e) => setExp((s) => ({ ...s, site: e.target.value }))}
                className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none bg-transparent"
              >
                <option value="">ALL</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="label-micro mb-1">CATEGORY</div>
              <select
                data-testid="exp-category"
                value={exp.category}
                onChange={(e) => setExp((s) => ({ ...s, category: e.target.value }))}
                className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none bg-transparent"
              >
                <option value="">ALL</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button
              data-testid="exp-download-btn"
              onClick={exportCsv}
              disabled={exporting}
              className="bg-black text-white font-mono text-xs uppercase tracking-widest font-bold px-4 py-2.5 hover:bg-olive transition-colors disabled:opacity-40 flex items-center justify-center gap-2 self-end"
            >
              <Download size={12} /> {exporting ? "EXPORTING…" : "DOWNLOAD"}
            </button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className=" w-full grid grid-cols-4 bg-white border border-slate-200 p-0 h-auto">
            <TabsTrigger data-testid="rep-eod" value="eod" className=" font-mono text-[11px] uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">EOD</TabsTrigger>
            <TabsTrigger data-testid="rep-eow" value="eow" className=" font-mono text-[11px] uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">EOW</TabsTrigger>
            <TabsTrigger data-testid="rep-eom" value="eom" className=" font-mono text-[11px] uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">EOM</TabsTrigger>
            <TabsTrigger data-testid="rep-recon" value="recon" className=" font-mono text-[11px] uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">RECON</TabsTrigger>
          </TabsList>

          <TabsContent value="eod" className="mt-4 space-y-4">
            {loading && !d && <div className="label-micro">LOADING…</div>}
            {d && tab === "eod" && (
              <div className="space-y-4">
                <Hero label="TOTAL SPEND / TODAY" value={formatINR(d.total_spend)} />
                <Grid>
                  <Cell label="TOTAL REQUESTS" value={d.total_requests} />
                  <Cell label="PENDING" value={d.pending} />
                  <Cell label="APPROVED" value={d.approved} />
                  <Cell label="REJECTED" value={d.rejected} />
                  <Cell label="PAID" value={d.paid} />
                  <Cell label="CLOSED" value={d.closed} />
                  <Cell label="EMERGENCY %" value={`${d.emergency_pct}%`} danger={d.emergency_pct > 10} />
                  <Cell label="SLA BREACH" value={d.sla_breaches} danger={d.sla_breaches > 0} />
                  <Cell label="POST-FACTO" value={d.post_facto_count} danger={d.post_facto_count > 0} />
                </Grid>
                <List title="SITE SPEND" items={d.site_spend} kField="site" onItemClick={openDrilldown} />
                <List title="CATEGORY SPEND" items={d.category_spend} kField="category" />
                {d.payment_modes && d.payment_modes.length > 0 && (
                  <ModeList items={d.payment_modes} />
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="eow" className="mt-4 space-y-4">
            {loading && !d && <div className="label-micro">LOADING…</div>}
            {d && tab === "eow" && (
              <div className="space-y-4">
                <Hero label="REQUESTS / LAST 7D" value={d.total_requests} />
                <List title="SITE SPEND" items={d.site_spend} kField="site" onItemClick={openDrilldown} />
                <List title="CATEGORY TRENDS" items={d.category_spend} kField="category" />
                <List title="TOP VENDORS" items={d.top_vendors} kField="vendor" />
                {d.payment_modes && d.payment_modes.length > 0 && (
                  <ModeList items={d.payment_modes} />
                )}
                <Grid>
                  <Cell label="POST-FACTO" value={d.violations.post_facto} danger={d.violations.post_facto > 0} />
                  <Cell label="SLA BREACH" value={d.violations.sla_breaches} danger={d.violations.sla_breaches > 0} />
                  <Cell label="SPLIT" value={d.violations.split} danger={d.violations.split > 0} />
                </Grid>
              </div>
            )}
          </TabsContent>

          <TabsContent value="eom" className="mt-4 space-y-4">
            {loading && !d && <div className="label-micro">LOADING…</div>}
            {d && tab === "eom" && (
              <div className="space-y-4">
                <Hero label="TOTAL SPEND / 30D" value={formatINR(d.total_spend)} />
                <Grid>
                  <Cell label="REQUESTS" value={d.total_requests} />
                  <Cell label="APPROVAL RATE" value={`${d.approval_rate}%`} />
                  <Cell label="AVG APPROVAL" value={`${d.avg_approval_hours}H`} />
                </Grid>
                <div>
                  <div className="label-micro mb-2">SITE PERFORMANCE</div>
                  <div className="bg-white border border-slate-200">
                    {d.site_performance.length === 0 ? <div className="p-5 label-micro text-center">NO DATA</div> :
                      d.site_performance.map((s) => (
                        <button
                          key={s.site}
                          data-testid={`site-perf-${s.site}`}
                          onClick={() => openDrilldown(s.site)}
                          className="w-full flex justify-between items-center px-4 py-3 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 text-left transition-colors group"
                        >
                          <div>
                            <div className="font-display font-bold text-sm uppercase">{s.site}</div>
                            <div className="label-micro">{s.count} REQUESTS</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="font-semibold">{formatINR(s.spend)}</div>
                            <ChevronRight size={14} className="text-slate-400 group-hover:text-black" />
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
                <Grid>
                  <Cell label="POST-FACTO" value={d.violations.post_facto} danger={d.violations.post_facto > 0} />
                  <Cell label="SLA BREACH" value={d.violations.sla_breaches} danger={d.violations.sla_breaches > 0} />
                  <Cell label="SPLIT" value={d.violations.split} danger={d.violations.split > 0} />
                </Grid>
                {d.payment_modes && d.payment_modes.length > 0 && (
                  <ModeList items={d.payment_modes} />
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="recon" className="mt-4">
            {tab === "recon" && (
              <ReconciliationPanel sites={sites} categories={categories} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Site drilldown modal */}
      <Dialog open={!!drilldown} onOpenChange={(o) => { if (!o) setDrilldown(null); }}>
        <DialogContent className="max-w-2xl  border border-slate-200 p-0 gap-0 max-h-[85vh] overflow-y-auto">
          <DialogHeader className="px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
            <DialogTitle className="font-semibold tracking-tight uppercase">
              {drilldown?.site} · {tab.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            {drilldownLoading && <div className="label-micro">LOADING…</div>}
            {drilldown?.data && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-slate-300 bg-white">
                  <Cell label="TOTAL SPEND" value={formatINR(drilldown.data.total_spend)} />
                  <Cell label="REQUESTS" value={drilldown.data.total_requests} />
                  <Cell label="APPROVED" value={drilldown.data.approved_count} />
                  <Cell label="PAID" value={drilldown.data.paid_count} />
                </div>
                {drilldown.data.categories?.length > 0 && (
                  <div>
                    <div className="label-micro mb-2">BY CATEGORY</div>
                    <div className="bg-white border border-slate-200">
                      {drilldown.data.categories.map((c) => (
                        <div key={c.category} className="flex justify-between items-center px-4 py-2.5 border-b border-slate-200 last:border-b-0">
                          <div>
                            <div className="font-mono text-xs font-bold">{c.category}</div>
                            <div className="label-micro">{c.count} payment{c.count === 1 ? "" : "s"}</div>
                          </div>
                          <div className="font-semibold text-sm">{formatINR(c.amount)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {drilldown.data.payment_modes?.length > 0 && (
                  <div>
                    <div className="label-micro mb-2">BY PAYMENT MODE</div>
                    <div className="bg-white border border-slate-200">
                      {drilldown.data.payment_modes.map((m) => (
                        <div key={m.mode} className="flex justify-between items-center px-4 py-2.5 border-b border-slate-200 last:border-b-0">
                          <div>
                            <div className="font-mono text-xs font-bold">{m.mode}</div>
                            <div className="label-micro">{m.count} payment{m.count === 1 ? "" : "s"}</div>
                          </div>
                          <div className="font-semibold text-sm">{formatINR(m.amount)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

const Hero = ({ label, value }) => (
  <div className="bg-white border border-slate-200 p-5">
    <div className="label-micro">{label}</div>
    <div className="font-semibold text-4xl tracking-tight mt-1">{value}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-3 gap-0 border-t border-l border-slate-300 bg-white">{children}</div>
);

const Cell = ({ label, value, danger }) => (
  <div className={`border-r border-b border-slate-300 p-3 ${danger ? "bg-red-50" : ""}`}>
    <div className="label-micro">{label}</div>
    <div className={`font-semibold text-xl tracking-tight mt-1 ${danger ? "text-red-600" : ""}`}>{value}</div>
  </div>
);

const List = ({ title, items, kField, onItemClick }) => (
  <div>
    <div className="label-micro mb-2">{title}</div>
    <div className="bg-white border border-slate-200">
      {!items || items.length === 0 ? <div className="p-5 label-micro text-center">NO DATA</div> :
        items.map((it) => {
          const value = it[kField];
          const amt = it.amount;
          const clickable = onItemClick && kField === "site";
          const inner = (
            <>
              <div className="font-mono text-xs font-bold uppercase">{value}</div>
              <div className="flex items-center gap-2">
                <div className="font-semibold text-sm">{formatINR(amt)}</div>
                {clickable && <ChevronRight size={12} className="text-slate-400 group-hover:text-black" />}
              </div>
            </>
          );
          if (clickable) {
            return (
              <button
                key={value}
                data-testid={`list-row-${value}`}
                onClick={() => onItemClick(value)}
                className="w-full flex justify-between items-center px-4 py-2.5 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 text-left group"
              >
                {inner}
              </button>
            );
          }
          return (
            <div key={value} className="flex justify-between items-center px-4 py-2.5 border-b border-slate-200 last:border-b-0">
              {inner}
            </div>
          );
        })}
    </div>
  </div>
);

const ModeList = ({ items }) => (
  <div>
    <div className="label-micro mb-2">PAYMENT MODE BREAKDOWN</div>
    <div className="bg-white border border-slate-200">
      {items.map((m) => (
        <div key={m.mode} data-testid={`mode-row-${m.mode}`} className="flex justify-between items-center px-4 py-2.5 border-b border-slate-200 last:border-b-0">
          <div>
            <div className="font-mono text-xs font-bold">{m.mode}</div>
            <div className="label-micro">{m.count} payment{m.count === 1 ? "" : "s"}</div>
          </div>
          <div className="font-semibold text-sm">{formatINR(m.amount)}</div>
        </div>
      ))}
    </div>
  </div>
);
